import React, { useState } from "react";
import get from "lodash/get";
import { useHistory, Switch, Route, useLocation } from "react-router-dom";

import { CLOUD_BUILD_OPERATIONS, ContentType } from "../../constants";
import { areYouSure, getMonthName, trim } from "../helpers";
import { getFileMetaText } from "./helpers";
import { deleteContent } from "../../apis/firebase";
import { closePopUp, showLoadingPopUp } from "../utils/Overlay";
import Tool from "../utils/icons/Tool";
import Type from "../utils/icons/Type";
import File from "../utils/icons/File";
import Kebab from "../utils/Kebab";

export default function ContentPreview({
  children,
  onEdit,
  content,
  post,
  onDelete,
  onUp,
  onDown,
  enhancements,
}: {
  children?: any;
  post?: Post;
  content: Content;
  onEdit?: Function;
  onDelete?: Function;
  onUp?: Function;
  onDown?: Function;
  enhancements?: boolean;
  verification?: boolean;
  key?: any;
}) {
  async function del() {
    showLoadingPopUp("Deleting. Please wait.");
    if (post) {
      await deleteContent(post.id, content);
    }
    closePopUp();
    if (onDelete) {
      onDelete();
    }
  }
  const history = useHistory();
  const contentType = get(content, "meta.contentType", "");
  const isImage = contentType.match(/^image/);
  const isAudio = contentType.match(/^audio/);
  const isVideo = contentType.match(/^video/);
  const isFile = content.type === ContentType.FILE;
  const options: KebabMenuLink[] = [];
  const deleteMessage = `You are about to delete ${
    content.meta?.name ? `"${content.meta?.name}"` : "a"
  } file. Are you sure?`;
  let leadingImage: JSX.Element | null = null,
    text: JSX.Element | null = null;

  function openEditor(op: string) {
    if (post) {
      history.push(
        `/campaigns/edit/${post.id}/editor?op=${op}&content=${content.id || ""}`
      );
    }
  }

  // ******************************* audio
  if (enhancements && isAudio) {
    options.push({
      label: "Change volume",
      onClick: () => openEditor(CLOUD_BUILD_OPERATIONS.audio_volume),
    });
    options.push({
      label: "Trim",
      onClick: () => openEditor(CLOUD_BUILD_OPERATIONS.cut_audio),
    });
    options.push({
      label: "Convert to video",
      width: 140,
      onClick: () => openEditor(CLOUD_BUILD_OPERATIONS.audio_photo_to_video),
    });
    options.push({
      label: "Merge audio files",
      onClick: () => openEditor(CLOUD_BUILD_OPERATIONS.audio_audio_to_audio),
    });
  }
  // ******************************* image
  if (enhancements && isImage) {
    options.push({
      label: "Edit",
      onClick: () => openEditor(CLOUD_BUILD_OPERATIONS.photo_manipulation),
    });
    options.push({
      label: "Convert to video",
      width: 140,
      onClick: () => openEditor(CLOUD_BUILD_OPERATIONS.audio_photo_to_video),
    });
  }
  // ******************************* video
  if (enhancements && isVideo) {
    options.push({
      label: "Change volume",
      onClick: () => openEditor(CLOUD_BUILD_OPERATIONS.video_volume),
      width: 140,
    });
    options.push({
      label: "Trim",
      onClick: () => openEditor(CLOUD_BUILD_OPERATIONS.cut_video),
    });
  }
  // ******************************* generic ones
  if (onEdit && content.type !== ContentType.FILE) {
    options.push({
      label: "Edit",
      onClick: onEdit,
    });
  }
  if (onDelete) {
    options.push({
      label: "Delete",
      onClick: areYouSure(del, deleteMessage),
    });
  }
  // ******************************* up and down
  if (onUp && !onDown) {
    options.push({
      label: "↑",
      onClick: onUp,
    });
  } else if (!onUp && onDown) {
    options.push({
      label: "↓",
      onClick: onDown,
    });
  } else if (onUp && onDown) {
    options.push([
      {
        label: "↑",
        onClick: onUp,
      },
      {
        label: "↓",
        onClick: onDown,
      },
    ]);
  }

  if (isFile) {
    const isImage = (content.meta?.contentType || "").match(/^image/);
    leadingImage = content.thumbnail ? (
      <img src={content.thumbnail} />
    ) : isImage && content.meta?.url ? (
      <img src={content.meta?.url} />
    ) : (
      <File />
    );
  } else if (content.type === ContentType.TEXT) {
    leadingImage = <Type size={14} />;
  }

  if (content.type === ContentType.TEXT) {
    text = (
      <>
        {trim(content.value, 80)}{" "}
        <small className="op06">({content.value.length})</small>
      </>
    );
  } else if (content.type === ContentType.FILE) {
    text = getFileMetaText(content);
  }

  return (
    <li id={content.id}>
      {leadingImage ? (
        <div className="leading-image center">{leadingImage}</div>
      ) : null}
      <div className="cf rel">
        <span className="ib mr05">
          {text}
          {formatUsed(content)}
        </span>
        {children}
        <ContentActions content={content} />
        {options.length > 0 && <Kebab links={options} width={100} />}
      </div>
    </li>
  );
}

function ContentActions({ content }: { content: Content }) {
  const actions = (content.actions || []).filter(
    (action) => action !== "CREATING_THUMBNAIL"
  );
  if (actions.length > 0) {
    return (
      <p className="fz08">
        <Tool className="blinking vam" size={14} />{" "}
        {actions.map(actionToLabel).join(", ")}
      </p>
    );
  }
  return null;
}
function actionToLabel(action: CONTENT_ACTIONS) {
  if (action === "CREATING_THUMBNAIL") return "Creating thumbnail";
  if (action === "CREATING_SNIPPET_IMAGE")
    return "Creating code snippet's image";
  if (action === "FILE_MANIPULATION") return "File manipulation in progress.";
  return action;
}
function formatUsed(content: Content) {
  if (content.used && content.used.length > 0) {
    return (
      <div className="ib ml05">
        {content.used.map((d, i) => {
          const date = d.toDate();
          return (
            <span key={i} className="used">
              ✔ {date.getDate() + " " + getMonthName(date)}
            </span>
          );
        })}
      </div>
    );
  }
  return null;
}
